import React, { useState, useEffect } from 'react';
import {
  addMuscle,
  updateMuscle,
  deleteMuscle,
  addMuscleGroup,
  updateMuscleGroup,
  deleteMuscleGroup,
  addMuscleToGroup,
  removeMuscleFromGroup,
  getMuscleGroupsWithMuscles,
  getIndexedDBData
} from '@utility/workout_utils';
import '@styles/MuscleManagement.css'
import { FaPlus } from "react-icons/fa";

const MuscleManagement = () => {
  const [groupsWithMuscles, setGroupsWithMuscles] = useState([]);
  const [standaloneMuscles, setStandaloneMuscles] = useState([]);
  const [newMuscleName, setNewMuscleName] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [editingMuscle, setEditingMuscle] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [newGroup, setNewGroup] = useState(false);
  const [selectedMuscleForGroup, setSelectedMuscleForGroup] = useState('');
  const [selectedGroupForMuscle, setSelectedGroupForMuscle] = useState('');
  const [addingMuscle, setAddingMuscle] = useState(false);
  const [deletingMuscle, setDeletingMuscle] = useState(null);
  const [deletingGroup, setDeletingGroup] = useState(null);
  
  const toggleAddMuscle = () => {
    setAddingMuscle(!addingMuscle);
  }
  // Load initial data
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      const data = await getMuscleGroupsWithMuscles();
      setGroupsWithMuscles(data.filter(group => group.id !== null));
      setStandaloneMuscles(
        data.find(group => group.id === null)?.muscles || []
      );
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };
  
  const handleAddMuscle = async () => {
    if (!newMuscleName.trim()) return;
    
    try {
      await addMuscle(newMuscleName);
      setNewMuscleName('');
      await loadData();
    } catch (error) {
      console.error("Error adding muscle:", error);
    }
    toggleAddMuscle();
  };
  
  const handleUpdateMuscle = async () => {
    if (!editingMuscle || !editingMuscle.name.trim()) return;
    
    try {
      await updateMuscle(editingMuscle.id, editingMuscle.name);
      setEditingMuscle(null);
      await loadData();
    } catch (error) {
      console.error("Error updating muscle:", error);
    }
  };
  
  const handleDeleteMuscle = async () => {
    if (!deletingMuscle) return;
    
    try {
      await deleteMuscle(deletingMuscle.id);
      setDeletingMuscle(null);
      await loadData();
    } catch (error) {
      console.error("Error deleting muscle:", error);
    }
  };
  
  const handleAddGroup = async () => {
    if (!newGroupName.trim()) return;
    
    try {
      await addMuscleGroup(newGroupName);
      setNewGroupName('');
      await loadData();
      setNewGroup(false);
    } catch (error) {
      console.error("Error adding group:", error);
    }
  };
  
  const handleUpdateGroup = async () => {
    if (!editingGroup || !editingGroup.name.trim()) return;
    
    try {
      await updateMuscleGroup(editingGroup.id, editingGroup.name);
      setEditingGroup(null);
      await loadData();
    } catch (error) {
      console.error("Error updating group:", error);
    }
  };
  
  const handleDeleteGroup = async () => {
    if (!deletingGroup) return;
    
    try {
      await deleteMuscleGroup(deletingGroup.id);
      setDeletingGroup(null);
      await loadData();
    } catch (error) {
      console.error("Error deleting group:", error);
    }
  };
  
  const handleAddMuscleToGroup = async () => {
    if (!selectedMuscleForGroup || !selectedGroupForMuscle) return;
    
    try {
      await addMuscleToGroup(parseInt(selectedMuscleForGroup), parseInt(selectedGroupForMuscle));
      await loadData();
      setSelectedMuscleForGroup('');
      setSelectedGroupForMuscle('');
    } catch (error) {
      console.error("Error adding muscle to group:", error);
    }
  };
  
  const handleRemoveFromGroup = async (muscleId, groupId) => {
    try {
      await removeMuscleFromGroup(muscleId, groupId);
      await loadData();
    } catch (error) {
      console.error("Error removing muscle:", error);
    }
  };

  const handleReassignMuscle = async (muscleId, newGroupId) => {
    try {
      // First remove from current group (if any)
      const currentGroup = groupsWithMuscles.find(g => 
        g.muscles.some(m => m.id === muscleId)
      );
      
      if (currentGroup) {
        await removeMuscleFromGroup(muscleId, currentGroup.id);
      }
      
      // Add to new group (if selected)
      if (newGroupId) {
        await addMuscleToGroup(muscleId, newGroupId);
      }
      
      await loadData();
    } catch (error) {
      console.error("Error reassigning muscle:", error);
    }
  };

  return (
    <div className="settings-content">
      <h2>Muscle Management</h2>

      <div className="group-card">
        <div className="button-box">
          <button className="edit-button" onClick={toggleAddMuscle}> <FaPlus /> Add New Muscle</button>
          <button className="edit-button" onClick={() => setNewGroup(true)}> <FaPlus /> Add New Group</button>
        </div>
      </div>
      
      <div className="section">

          {deletingMuscle && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Delete Muscle</h3>
              <p>Are you sure you want to delete {deletingMuscle.name}? This will remove it from all exercises, you will have to reassign to exercises if you wish to add it back.</p>
              <div className="button-box">
                <button className="delete-button" onClick={() => setDeletingMuscle(null)}>
                  Cancel
                </button>
                <button className="remove-button" onClick={handleDeleteMuscle}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {deletingGroup && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Delete Muscle Group</h3>
              <p>Are you sure you want to delete {deletingGroup.name}? This will remove all muscle associations.</p>
              <div className="button-box">
                <button className="delete-button" onClick={() => setDeletingGroup(null)}>
                  Cancel
                </button>
                <button className="remove-button" onClick={handleDeleteGroup}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {editingMuscle ? (
          <>
            <div className="modal-overlay">
                <div className="modal-content">
                  <h3>Rename Muscle</h3>
                  <div className="edit-form">
                    <input
                      type="text"
                      value={editingMuscle.name}
                      onChange={(e) => setEditingMuscle({...editingMuscle, name: e.target.value})}
                    />
                  </div>
                  <div className="button-box">
                    <button className="remove-button" onClick={() => setEditingMuscle(null)}>Cancel</button>
                    <button className="edit-button"  onClick={handleUpdateMuscle}>Save</button>
                  </div>
                </div>
            </div>
          </>
        ) : (
          <>
          {!addingMuscle ? (
            <>
            </>
            ) :
            ( 
            <>
            <div className="modal-overlay">
                <div className="modal-content">
                <h3>Add Muscle</h3>
                <p>After creating new muscles you can assign them to different groups and exercises.</p>
                <div className="add-form">
                  <input
                    type="text"
                    value={newMuscleName}
                    onChange={(e) => setNewMuscleName(e.target.value)}
                    placeholder="New muscle name"
                  />
                </div>
                <div className="button-box">
                  <button className="remove-button" onClick={toggleAddMuscle}>Cancel</button>
                  <button className="edit-button" onClick={handleAddMuscle}>Add Muscle</button>
                </div>
              </div>
            </div>
            </>
            )
            }
          </>
        )}

        {editingGroup ? (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Rename Muscle Group</h3>
              <div className="edit-form">
                <input
                  type="text"
                  value={editingGroup.name}
                  onChange={(e) => setEditingGroup({...editingGroup, name: e.target.value})}
                />
              </div>
              <div className="button-box">
                <button className="remove-button" onClick={() => setEditingGroup(null)}>Cancel</button>
                <button className="edit-button" onClick={handleUpdateGroup}>Save</button>
              </div>
            </div>
          </div>
        ) : null}

        {newGroup ? (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>New Muscle Group</h3>
              <p>Muscle groups are used to group specific muscles into larger areas.</p>
              <div className="add-form">
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="New group name"
                />
              </div>
              <div className="button-box">
                <button className="remove-button" onClick={() => setNewGroup(false)}>Cancel</button>
                <button className="edit-button" onClick={handleAddGroup}>Add Group</button>
              </div>
            </div>
          </div>
        ) : null}
        
        <h2>Muscle Groups</h2>

        <div className="group-container">
          {standaloneMuscles.length > 0 && (
            <div className="group-card">
              <div className="group-header">
                <h3>Ungrouped Muscles</h3>
              </div>
              <ul className="muscle-list">
                {standaloneMuscles.map(muscle => (
                  <li key={muscle.id}>
                    <span>{muscle.name}</span>
                    <div className="button-box-muscle">
                      <select
                        value=""
                        onChange={(e) => {
                          const newGroupId = e.target.value ? parseInt(e.target.value) : null;
                          handleReassignMuscle(muscle.id, newGroupId);
                        }}
                      >
                        <option value="">Ungrouped</option>
                        {groupsWithMuscles.map(g => (
                          <option key={g.id} value={g.id}>{g.name}</option>
                        ))}
                      </select>
                      <button className="edit-button" onClick={() => setEditingMuscle(muscle)}>Rename</button>
                      <button className="remove-button" onClick={() => setDeletingMuscle(muscle)}>Delete</button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {groupsWithMuscles.map(group => (
            <div key={group.id} className="group-card">
              <div className="group-header">
                <h3>{group.name}</h3>
                <div className="button-box">
                  <button className="delete-button" onClick={() => setDeletingGroup(group)}>
                    Delete
                  </button>
                  <button className="edit-button" onClick={() => setEditingGroup(group)}>
                    Rename
                  </button>
                </div>
              </div>
              
              <ul className="muscle-list">
                {group.muscles.map(muscle => (
                  <li key={`group-${group.id}-muscle-${muscle.id}`}>
                    <span>{muscle.name}</span>
                    <div className="      ">
                       <div className="button-box-muscle">
                        <select
                          value={group.id}
                          onChange={(e) => {
                            const newGroupId = e.target.value ? parseInt(e.target.value) : null;
                            handleReassignMuscle(muscle.id, newGroupId);
                          }}
                          
                        >
                        <option value="">Ungrouped</option>
                        {groupsWithMuscles.map(g => (
                          <option 
                            key={g.id} 
                            value={g.id}
                            disabled={g.id === group.id}
                          >
                            {g.name}
                          </option>
                        ))}
                      </select>
                      <button className="edit-button" onClick={() => setEditingMuscle(muscle)}>Rename</button>
                      <button className="remove-button" onClick={() => setDeletingMuscle(muscle)}>Delete</button>
                    </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MuscleManagement;